import { useCompanyApi } from '@/api/client/api-client';
import {
    type DocumentTemplate,
    type DocumentTemplateResponse,
    type ImageSizes,
    TEMPLATE_CONVENTION_PREFIX,
} from '@/interfaces/document-management/document-management.types';
import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

const DOC_MANAGEMENT_BASE_URL = '/api/documents';

export const useDocumentManagementService = () => {
    const apiClient = useCompanyApi({ auth: 'jwt' });

    const getDocumentTemplates = ({
        page = 0,
        pageSize = 100,
    }: {
        page: MaybeRefOrGetter<number>;
        pageSize: MaybeRefOrGetter<number>;
    }) => {
        const url = computed(
            () =>
                `${DOC_MANAGEMENT_BASE_URL}/templates?page=${toValue(page)}&pageSize=${toValue(pageSize)}&searchText=${TEMPLATE_CONVENTION_PREFIX}`,
        );

        return apiClient(url, { refetch: true, immediate: false }).json<DocumentTemplateResponse>();
    };

    const downloadDocumentForTemplateId = async (templateId: DocumentTemplate['id']) => {
        const { data, error } = await apiClient(`${DOC_MANAGEMENT_BASE_URL}/download?templateId=${templateId}`).get().blob();
        if (error.value) {
            throw error.value;
        }

        const blob = data.value as Blob;
        if (!blob) throw new Error('Empty file');
        return blob;
    };

    const previewFirstPage = ({ templateId, size = 'medium' }: { templateId: DocumentTemplate['id']; size: ImageSizes }) => {
        return apiClient(`${DOC_MANAGEMENT_BASE_URL}/previewFirstPage?templateId=${templateId}&size=${size}`)
            .get()
            .json<DocumentTemplateResponse>();
    };

    return {
        getDocumentTemplates,
        previewFirstPage,
        downloadDocumentForTemplateId,
    };
};
